import { type QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, Outlet, useMatches, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { suscribirseACambiosSesion } from '../features/auth/api/sesionCanal';
import { useSalir } from '../features/auth/api/useAcceso';
import { useSesion } from '../features/auth/api/useSesion';
import { AvisoModoVisitante } from '../features/auth/components/avisoModoVisitante';
import { AvisoCookies } from '../features/legal/components/avisoCookies';
import { PieLegal } from '../features/legal/components/pieLegal';
import { Cabecera } from '../features/shell/components/cabecera';

/**
 * Contexto del router (oleada 3): sin él no hay `beforeLoad` con datos propios, y sin
 * `beforeLoad` no hay guards de ruta. `main.tsx` lo rellena con el `QueryClient` de la app al
 * llamar a `createRouter`; `_autenticado.tsx` y `_autenticado/admin.tsx` lo consumen para
 * comprobar la sesión contra el servidor con `ensureQueryData`.
 */
export interface RouterContext {
	queryClient: QueryClient;
}

/**
 * F-1: cada ruta declara su propia naturaleza de layout en vez de que `__root.tsx` mantenga una
 * lista de rutas especiales. `useMatches()` expone `staticData` (opciones de `createFileRoute`
 * que no varían con los parámetros) en cada coincidencia de la rama activa, así que el `Layout`
 * solo tiene que preguntar si ALGUNA coincidencia la activó.
 */
declare module '@tanstack/react-router' {
	interface StaticDataRouteOption {
		/** `/login`: la pantalla se pinta a sangre, sin el contenedor de lectura de `<main>`. */
		anchoCompleto?: boolean;
		/** `/login`, `/restablecer-contrasena`: el aviso de modo visitante no se pinta. */
		rutaDeAcceso?: boolean;
	}
}

const Layout = (): React.JSX.Element => {
	const { queryClient } = Route.useRouteContext();
	const { sesion } = useSesion();
	const salir = useSalir();
	const navigate = useNavigate();
	const matches = useMatches();
	const anchoCompleto = matches.some(match => match.staticData.anchoCompleto === true);
	const rutaDeAcceso = matches.some(match => match.staticData.rutaDeAcceso === true);

	// CL-4 (documento 02b, §12): cerrar sesión en una pestaña se nota en las demás. Vive aquí,
	// en el layout raíz, y no en `main.tsx`, porque `suscribirseACambiosSesion` está pensada
	// para un `useEffect` (devuelve una función de limpieza que cierra el canal).
	useEffect(() => suscribirseACambiosSesion(queryClient), [queryClient]);

	// `useSalir` ya resetea la query de sesión y avisa a las demás pestañas por su cuenta (ver su
	// JSDoc); aquí solo hace falta añadir la navegación a `/login` tras el éxito.
	const manejarCerrarSesion = (): void => {
		salir.mutate(undefined, { onSuccess: () => void navigate({ to: '/login' }) });
	};

	return (
		<div className='flex min-h-screen flex-col bg-surface-app'>
			<Cabecera onCerrarSesion={manejarCerrarSesion} sesion={sesion} />
			{/* P-01: el aviso de cookies se monta SIEMPRE, en todas las rutas — a diferencia de
			 * `AvisoModoVisitante`, no depende de si hay sesión ni se apaga en `rutaDeAcceso`
			 * (`/login`): el consentimiento de cookies es del navegador, no de la cuenta, y «seguir
			 * navegando no es consentir» tiene que cumplirse también en la puerta de entrada. Mismo
			 * envoltorio `<div>` sin clases que `AvisoModoVisitante` más abajo, y por el mismo motivo
			 * (ver el comentario largo de ese bloque): la raíz de `AvisoCookies` lleva `mx-auto
			 * max-w-3xl px-4 sm:px-6`, y ese `mx-auto` no puede ir en un hijo DIRECTO del contenedor
			 * raíz `flex flex-col` de este layout. */}
			<div>
				<AvisoCookies />
			</div>
			{/* CA-02b.11 + H-26 → T-26 → H-33 → T-31: una única instancia, siempre en flujo,
			 * entre la cabecera y `<main>`, en todos los anchos — se ve en el primer pantallazo
			 * y desplaza el contenido en vez de taparlo. Se autogestiona con `useSesion`, así
			 * que montarlo aquí basta para las rutas abiertas de hoy y las que lleguen en
			 * iteraciones futuras. F-1: `/login` (`rutaDeAcceso`) no lo monta — no hay sesión
			 * de visitante que avisar en la propia puerta de entrada.
			 *
			 * Envoltorio propio (`<div>` sin clases) entre este condicional y `AvisoModoVisitante`:
			 * la raíz de ese componente es `mx-auto max-w-3xl px-4 sm:px-6` (para alinearse con
			 * `<main>`, ver su propio JSDoc), y ese `mx-auto` son márgenes automáticos en el eje
			 * transversal. La raíz de este layout es `flex min-h-screen flex-col`, así que
			 * cualquier hijo DIRECTO suyo es un flex-item — y un flex-item con márgenes automáticos
			 * en el eje transversal deja de estirarse (spec de Flexbox §8.3) y se encoge a su
			 * contenido: medido en `/saber-mas` a 1100 px, la pastilla quedaba en 749 px de ancho
			 * frente a los 768 px de `<main>`, 19 px más estrecha y descentrada 10 px por lado. Es
			 * la misma trampa ya vivida en `decoradoFranjaHoraria.tsx` y en el JSDoc de
			 * `routes/login.tsx` (ahí encogía la tarjeta de acceso de 448 a ~292 px), colada aquí
			 * por otro camino. Este `<div>` sin `mx-auto` es el flex-item real (se estira sin
			 * problema) y dentro de él el `mx-auto` de `AvisoModoVisitante` vuelve a resolverse en
			 * flujo de bloque normal, no en flex — por eso no vale tocar `avisoModoVisitante.tsx`
			 * (fuera de alcance, y además la corrección quedaría atada a *ese* componente: cualquier
			 * otro que en el futuro devuelva una raíz con `mx-auto` y se monte aquí volvería a caer
			 * en la misma trampa; el envoltorio la cierra para todos) ni quitar el `flex` de la
			 * raíz (es lo que permite que `<main className='flex-1'>` llene el alto restante bajo
			 * la cabecera, de lo que depende el degradado a sangre de `/login`). Regla general:
			 * ningún hijo directo del contenedor raíz `flex flex-col` puede llevar márgenes
			 * automáticos en el eje transversal (`mx-auto` y compañía). */}
			{rutaDeAcceso ? null : (
				<div>
					<AvisoModoVisitante />
				</div>
			)}
			{/* F-1: el contenedor de lectura (`max-w-3xl px-4 py-8 sm:px-6`) ya no vive en
			 * `<main>` — vive en un `<div>` interior, condicionado a `anchoCompleto`, para que
			 * `/login` pueda pintar su degradado a sangre completa (todo el ancho, mínimo el alto
			 * de la ventana bajo la cabecera) sin que ninguna otra ruta cambie de aspecto: las
			 * clases del contenedor son exactamente las mismas que antes llevaba `<main>`.
			 * Dos ternarios hermanos (nunca anidados: `lint/style/noNestedTernary` de Biome).
			 *
			 * F-1: `<main>` es `grid grid-rows-1 flex-1` SOLO en la rama `anchoCompleto`; en la
			 * otra sigue siendo `flex-1` a secas. Causa raíz de por qué hacía falta esto:
			 * `decoradoFranjaHoraria.tsx` declara `min-h-full` para que su degradado llegue al pie
			 * de la ventana, pero `min-height: 100%` no resuelve contra un padre de altura `auto`
			 * — y `flex-1` le da a `<main>` una altura repartida por el algoritmo flex
			 * (`height: auto`, no una altura especificada), así que el porcentaje del hijo no
			 * tenía contra qué resolver. Medido en `/login`: `<main>` 1013 px de alto, decorado
			 * 769 px, 244 px de franja gris al pie de la ventana. Una rejilla de una sola pista sí
			 * da a su hijo una altura especificada (la pista ocupa el 100 % del alto del
			 * contenedor), así que el hijo se estira sin depender de un porcentaje.
			 *
			 * Por qué SOLO en la rama `anchoCompleto`: su único hijo es el decorado, que no lleva
			 * márgenes automáticos — como grid-item se estira sin problema. La rama normal NO se
			 * toca porque su hijo es el contenedor de lectura `mx-auto max-w-3xl px-4 py-8
			 * sm:px-6`: un hijo con márgenes automáticos en el eje transversal deja de estirarse
			 * (spec de Flexbox §8.3; en rejilla pasa lo mismo vía `justify-self`) y se encogería a
			 * su contenido. Es la misma trampa que este repositorio ya ha pagado tres veces: la
			 * tarjeta de acceso de 448 a ~292 px, el aviso de visitante de 768 a 749 px — ambas
			 * documentadas en `decoradoFranjaHoraria.tsx` y en el JSDoc de `routes/login.tsx`. Por
			 * dentro, en la rama normal `<main>` se queda en flujo de bloque normal. */}
			<main className={anchoCompleto ? 'grid grid-rows-1 flex-1' : 'flex-1'}>
				{anchoCompleto ? (
					<Outlet />
				) : (
					<div className='mx-auto max-w-3xl px-4 py-8 sm:px-6'>
						<Outlet />
					</div>
				)}
			</main>
			{/* P-01: el pie con los cinco documentos legales no se pinta en la rama `anchoCompleto`
			 * (hoy solo `/login`) — esa pantalla se pinta a sangre, con el decorado llenando
			 * exactamente el alto restante bajo la cabecera (ver el JSDoc largo de `<main>` más
			 * arriba); un pie debajo rompería justo eso, la razón de ser de `anchoCompleto`. `PieLegal`
			 * es un `<footer>`, sin `mx-auto` propio (ver su JSDoc), así que puede ir aquí, hijo
			 * directo del contenedor raíz, sin caer en la misma trampa que `AvisoModoVisitante`. */}
			{anchoCompleto ? null : <PieLegal />}
		</div>
	);
};

export const Route = createRootRouteWithContext<RouterContext>()({ component: Layout });
